import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { Seo, SITE, breadcrumb } from "@/components/Seo";
import { ArrowLeft, ArrowRight, Loader2, Gift } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return <div className="max-w-[760px] mx-auto px-6 py-24 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }
  if (post === null) {
    return (
      <div className="max-w-[760px] mx-auto px-6 py-24 text-center" data-testid="blog-notfound">
        <h1 className="font-heading font-bold text-2xl">Article not found</h1>
        <Link to="/resources" className="inline-flex items-center gap-2 text-primary font-semibold mt-4"><ArrowLeft className="w-4 h-4" /> Back to the blog</Link>
      </div>
    );
  }

  return (
    <article className="max-w-[760px] mx-auto px-6 lg:px-8 py-16" data-testid="blog-post">
      <Seo
        title={`${post.seo_title || post.title} — Centennial Business Systems`}
        description={post.meta_description || post.excerpt || ""}
        canonicalPath={`/blog/${post.slug}`}
        image={post.cover_image || undefined}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.meta_description || post.excerpt || "",
            image: post.cover_image ? [post.cover_image] : undefined,
            datePublished: post.date || undefined,
            author: { "@type": post.author ? "Person" : "Organization", name: post.author || "Centennial Business Systems" },
            publisher: {
              "@type": "Organization",
              name: "Centennial Business Systems",
              logo: { "@type": "ImageObject", url: `${SITE}/cdos-logo.png` },
            },
          },
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Resources", path: "/resources" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <Link to="/resources" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors">
        <ArrowLeft className="w-4 h-4" /> All articles
      </Link>

      {post.category && <div className="text-[11px] uppercase tracking-wider font-semibold text-primary mt-8">{post.category}</div>}
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-2 leading-[1.1]">{post.title}</h1>
      <div className="text-sm text-muted-foreground mt-4">
        {post.author} · {fmtDate(post.date)}
      </div>

      {post.cover_image && (
        <div className="mt-8 aspect-[3/2] overflow-hidden bg-secondary border border-border" data-testid="blog-cover">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="blog-body mt-10" data-testid="blog-body">
        <ReactMarkdown>{post.body}</ReactMarkdown>
      </div>

      {post.keywords?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-10">
          {post.keywords.map((k) => (
            <span key={k} className="text-xs bg-secondary border border-border px-2.5 py-1 text-muted-foreground">{k}</span>
          ))}
        </div>
      )}

      {/* Organic CTA */}
      <div className="mt-12 bg-primary text-white p-7" data-testid="blog-cta">
        <div className="flex items-center gap-2 font-heading font-bold text-lg"><Gift className="w-5 h-5" /> Put this into practice today</div>
        <p className="text-white/80 text-sm mt-2">Get the actual dealership forms behind this article — claim any 3 free, no card needed.</p>
        <div className="flex flex-wrap gap-3 mt-4">
          <button onClick={() => navigate("/forms")} data-testid="blog-cta-forms"
            className="inline-flex items-center gap-2 bg-white text-primary px-5 py-3 font-semibold btn-lift">
            Claim 3 forms free <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate("/products")} data-testid="blog-cta-products"
            className="inline-flex items-center gap-2 border border-white/40 px-5 py-3 font-semibold hover:bg-white/10 transition-colors">
            See the systems
          </button>
        </div>
      </div>
    </article>
  );
}
